import React, { useEffect, useState } from "react";
import axios from "axios";

interface SearchAnalyticsData {
  id: number;
  searchDate: string;
  origin: string;
  destination: string;
  isOneWay: boolean;
  count: number;
}

const SearchAnalytics = () => {
  const [data, setData] = useState<SearchAnalyticsData[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterDate, setFilterDate] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterDestination, setFilterDestination] = useState("");
  const [filterType, setFilterType] = useState(""); // "", "true", "false"

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterDate) params.append("date", filterDate);
      if (filterOrigin) params.append("origin", filterOrigin);
      if (filterDestination) params.append("destination", filterDestination);
      if (filterType !== "") params.append("isOneWay", filterType);

      const res = await axios.get(`${baseUrl}/seo/admin/search-analytics?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setData(res.data);
    } catch (e) {
      console.error("Failed to fetch analytics", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const totalSearches = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Search Analytics</h1>
      
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
        <h2 className="text-xl font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Search</label>
            <input 
              type="date" 
              className="border border-gray-300 p-2 rounded w-full"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Origin (Code)</label>
            <input 
              type="text" 
              placeholder="e.g. CMB"
              maxLength={3}
              className="border border-gray-300 p-2 rounded w-full uppercase"
              value={filterOrigin}
              onChange={(e) => setFilterOrigin(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination (Code)</label>
            <input 
              type="text" 
              placeholder="e.g. LHR"
              maxLength={3}
              className="border border-gray-300 p-2 rounded w-full uppercase"
              value={filterDestination}
              onChange={(e) => setFilterDestination(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip Type</label>
            <select
              className="border border-gray-300 p-2 rounded w-full bg-white"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">One Way</option>
              <option value="false">Return</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-between items-center">
            <div className="flex gap-4">
              <button 
                  onClick={() => fetchAnalytics()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700"
              >
                  Search
              </button>
              <button 
                  onClick={() => {
                      setFilterDate("");
                      setFilterOrigin("");
                      setFilterDestination("");
                      setFilterType("");
                      // Optionally fetch immediately when clearing, or wait for them to hit search again.
                      // We'll leave it in the input state until they search.
                  }}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                  Clear Filters
              </button>
            </div>
            <div className="text-lg font-semibold text-blue-800 bg-blue-50 px-4 py-2 rounded-lg">
                Total Searches: {totalSearches}
            </div>
        </div>
      </div>

      {loading ? (
        <p>Loading analytics...</p>
      ) : data.length === 0 ? (
        <p>No search data found for the given criteria.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Date</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Route</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Trip Type</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Searches</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4">{new Date(item.searchDate).toLocaleDateString()}</td>
                  <td className="py-3 px-4 font-medium">{item.origin} → {item.destination}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${item.isOneWay ? "bg-purple-100 text-purple-800" : "bg-indigo-100 text-indigo-800"}`}>
                        {item.isOneWay ? "One Way" : "Return"}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-bold text-gray-700">{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SearchAnalytics;
